import BrandValidator from '@/utils/brandValidator';

/**
 * Vue directive to check if current site is a brand site
 * 
 * Usage examples:
 * - v-brand-site - Shows element only if it's a brand site
 * - v-brand-site.hide - Hides element if it's a brand site  
 * - v-brand-site.agent - Shows element only if it's an agent brand site
 * - v-brand-site.lender - Shows element only if it's a lender brand site
 * - v-brand-site.broker - Shows element only if it's a broker brand site
 * - v-brand-site.team - Shows element only if it's a team brand site
 * - v-brand-site.office - Shows element only if it's an office brand site
 * - v-brand-site.disable - Disables element if it's a brand site
 * - v-brand-site.class="my-class" - Adds class if it's a brand site
 */

function checkIsBrandSite(path = window.location.pathname) {
    return BrandValidator.isValid(path);
}

function getBrandInfo(path = window.location.pathname) {
    const result = BrandValidator.parse(path);
    return result.isValid ? result : null;
}

function shouldShowElement(el, binding) {
    const isBrandSite = checkIsBrandSite();
    const brandInfo = getBrandInfo();
    
    let shouldShow = true;
    
    // Check if we should hide on brand sites
    if (binding.modifiers.hide) {
        shouldShow = !isBrandSite;
    } else {
        // Default behavior: show only on brand sites
        shouldShow = isBrandSite;
    }
    
    // Check for specific brand type modifiers
    if (shouldShow && isBrandSite && brandInfo) {
        const brandTypeModifiers = ['agent', 'lender', 'broker', 'team', 'office'];
        const hasTypeModifier = brandTypeModifiers.some(type => binding.modifiers[type]);
        
        if (hasTypeModifier) {
            shouldShow = brandTypeModifiers.some(type => 
                binding.modifiers[type] && brandInfo.type.name === type
            );
        }
    }
    
    return shouldShow;
}

function updateElementVisibility(el, binding) {
    const shouldShow = shouldShowElement(el, binding);
    const isBrandSite = checkIsBrandSite();
    const brandInfo = getBrandInfo();
    
    // Handle visibility
    if (shouldShow) {
        el.style.display = el._originalDisplay || '';
        el.removeAttribute('hidden');
    } else {
        if (!el._originalDisplay) {
            el._originalDisplay = el.style.display;
        }
        el.style.display = 'none';
        el.setAttribute('hidden', 'true');
    }
    
    // Handle disable modifier
    if (binding.modifiers.disable) {
        if (isBrandSite) {
            el.disabled = true;
            el.setAttribute('disabled', 'true');
            el.classList.add('disabled');
        } else {
            el.disabled = false;
            el.removeAttribute('disabled');
            el.classList.remove('disabled');
        }
    }
    
    // Handle class modifier
    if (binding.value && typeof binding.value === 'string') {
        if (isBrandSite) {
            el.classList.add(binding.value);
        } else {
            el.classList.remove(binding.value);
        }
    }
    
    // Store brand info on element for potential use by other code
    el._brandSiteInfo = {
        isBrandSite,
        brandInfo,
        shouldShow
    };
}

export default {
    bind: function (el, binding, vnode) {
        // Store original display style
        el._originalDisplay = el.style.display;
        
        // Initial update
        updateElementVisibility(el, binding);
        
        // Listen for route changes if available
        if (vnode.context && vnode.context.$router) {
            const unwatch = vnode.context.$router.afterEach(() => {
                updateElementVisibility(el, binding);
            });
            
            // Store unwatch function for cleanup
            el._routerUnwatch = unwatch;
        }
    },
    
    update: function (el, binding) {
        updateElementVisibility(el, binding);
    },
    
    unbind: function (el) {
        // Cleanup
        if (el._routerUnwatch) {
            el._routerUnwatch();
        }
        
        // Restore original display
        if (el._originalDisplay !== undefined) {
            el.style.display = el._originalDisplay;
        }
        
        // Remove attributes we might have added
        el.removeAttribute('hidden');
        el.removeAttribute('disabled');
        el.classList.remove('disabled');
        
        // Clean up stored data
        delete el._brandSiteInfo;
        delete el._originalDisplay;
        delete el._routerUnwatch;
    }
}; 